import { useSessionStore } from "@/stores/sessionStore"

/**
 * Permissões finas derivadas das roles do Keycloak (UX apenas).
 * A segurança real é sempre validada no backend (@PreAuthorize + ownership).
 *
 * ──────────────────────────────────────────────────────────────────
 * REGRA: ao criar um novo módulo, SEMPRE adicione suas permissões aqui.
 * ──────────────────────────────────────────────────────────────────
 *
 * Passos ao adicionar um novo módulo:
 *
 *   1. Adicione o domínio em PERMISSOES (objeto abaixo) com suas ações:
 *        modulo: { consultar: "modulo:consultar", criar: "modulo:criar", ... }
 *
 *   2. Mapeie cada permissão para as roles em ROLE_POR_PERMISSAO:
 *        [PERMISSOES.modulo.consultar]: [ROLE_ADMIN, ROLE_MODULO],
 *
 *   3. No router.tsx, envolva as rotas do módulo com <RequirePermission>:
 *        { element: <RequirePermission any={[PERMISSOES.modulo.consultar]} />, children: [...] }
 *
 *   4. Em src/app/layouts/nav.ts, use usePermissions().has() para filtrar itens do menu.
 *
 * has(permissão) vale se:
 *   (a) a permissão veio pronta em /me (subrole SIGI_<MODULO>_<ACAO> atribuída ao
 *       setor no Keycloak — client roles do sigi-api) — sessionStore.permissions; OU
 *   (b) o usuário tem um perfil (ROLE_POR_PERMISSAO) que a derive — baseline do
 *       módulo (SIGI_<MODULO>, só *:consultar) ou SIGI_ADMIN (tudo).
 *
 * @see src/app/router.tsx               — guards de rota
 * @see src/app/guards/RequirePermission.tsx — componente guard
 * @see src/app/layouts/nav.ts          — filtragem do menu lateral
 */
export const PERMISSOES = {
  admin: {
    gerenciar: "sigi:admin:gerenciar",
  },
  inspecao: {
    consultar: "sigi:inspecao:consultar",
    criar: "sigi:inspecao:criar",
    editar: "sigi:inspecao:editar",
    excluir: "sigi:inspecao:excluir",
    emitir: "sigi:inspecao:emitir",
    notificar: "sigi:inspecao:notificar",
    agendar: "sigi:inspecao:agendar",
    sortear: "sigi:inspecao:sortear",
    groupassociation: "sigi:inspecao:groupassociation",
  },
  agendamento: {
    consultar: "sigi:agendamento:consultar",
    criar: "sigi:agendamento:criar",
    editar: "sigi:agendamento:editar",
    cancelar: "sigi:agendamento:cancelar",
    notificar: "sigi:agendamento:notificar",
    excluir: "sigi:agendamento:excluir",
    groupassociation: "sigi:agendamento:groupassociation",
  },
  checklist: {
    consultar: "sigi:checklist:consultar",
    groupassociation: "sigi:checklist:groupassociation",
  },
  pbdoc: {
    consultar: "sigi:pbdoc:consultar",
  },
  seguir: {
    consultar: "sigi:seguir:consultar",
  },
  epi: {
    solicitar: "sigi:epi:solicitar",
    consultar: "sigi:epi:consultar",
    gerenciar: "sigi:epi:gerenciar",
    entregar: "sigi:epi:entregar",
    descartar: "sigi:epi:descartar",
    estoque: "sigi:epi:estoque",
    importar: "sigi:epi:importar",
  },
  relatorio: {
    consultar: "sigi:relatorio:consultar",
    gerar: "sigi:relatorio:gerar",
    baixar: "sigi:relatorio:baixar",
  },
  almoxarifado: {
    consultar: "sigi:almoxarifado:consultar",
    categoriaGerenciar: "sigi:almoxarifado:categoria:gerenciar",
    equipamentoCriar: "sigi:almoxarifado:equipamento:criar",
    equipamentoEditar: "sigi:almoxarifado:equipamento:editar",
    estoqueGerenciar: "sigi:almoxarifado:estoque:gerenciar",
    movimentar: "sigi:almoxarifado:movimentar",
    solicitar: "sigi:almoxarifado:solicitar",
    aprovar: "sigi:almoxarifado:aprovar",
    entregar: "sigi:almoxarifado:entregar",
    relatorio: "sigi:almoxarifado:relatorio",
    grupoGerenciar: "sigi:almoxarifado:grupo:gerenciar",
    groupassociation: "sigi:almoxarifado:groupassociation",
  },
  cadastro: {
    localidadeConsultar: "cadastro:localidade:consultar",
    localidadeGerenciar: "cadastro:localidade:gerenciar",
    cidadeConsultar: "cadastro:cidade:consultar",
    cidadeGerenciar: "cadastro:cidade:gerenciar",
    empresaConsultar: "cadastro:empresa:consultar",
    empresaGerenciar: "cadastro:empresa:gerenciar",
    institucionalConsultar: "cadastro:institucional:consultar",
    institucionalGerenciar: "cadastro:institucional:gerenciar",
    expedienteConsultar: "cadastro:expediente:consultar",
    expedienteGerenciar: "cadastro:expediente:gerenciar",
    documentoConsultar: "cadastro:documento:consultar",
    documentoGerenciar: "cadastro:documento:gerenciar",
  },
  incidente: {
    consultar: "sigi:incidente:consultar",
    criar: "sigi:incidente:criar",
    editar: "sigi:incidente:editar",
    excluir: "sigi:incidente:excluir",
    investigar: "sigi:incidente:investigar",
  },
  rh: {
    consultar: "sigi:rh:consultar",
    criar: "sigi:rh:criar",
    editar: "sigi:rh:editar",
    excluir: "sigi:rh:excluir",
    importar: "sigi:rh:importar",
  },
  forms: {
    consultar: "sigi:forms:consultar",
    criar: "sigi:forms:criar",
    editar: "sigi:forms:editar",
    excluir: "sigi:forms:excluir",
    publicar: "sigi:forms:publicar",
    assinar: "sigi:forms:assinar",
    deferir: "sigi:forms:deferir",
    funcionarioListar: "sigi:forms:funcionario:listar",
    notification: "sigi:forms:notification",
    groupassociation: "sigi:forms:groupassociation",
  },
} as const

type Permissao = {
  [K in keyof typeof PERMISSOES]: (typeof PERMISSOES)[K][keyof (typeof PERMISSOES)[K]]
}[keyof typeof PERMISSOES]

const ROLE_ADMIN = "SIGI_ADMIN"

// Baseline por módulo (realm role SIGI_<MODULO> — entrada no menu/rota, só
// *:consultar), espelha Permissions do backend. As ações finas chegam em
// sessionStore.permissions (subroles SIGI_<MODULO>_<ACAO> do /me).
const ROLE_INSPECAO = "SIGI_INSPECAO"
const ROLE_AGENDAMENTO = "SIGI_AGENDAMENTO"
const ROLE_CHECKLIST = "SIGI_CHECKLIST"
const ROLE_EPI = "SIGI_EPI"
const ROLE_PBDOC = "SIGI_PBDOC"
const ROLE_SEGUIR = "SIGI_SEGUIR"
const ROLE_ALMOXARIFADO = "SIGI_ALMOXARIFADO"
const ROLE_CADASTRO = "SIGI_CADASTRO"
const ROLE_INCIDENTE = "SIGI_INCIDENTE"
const ROLE_RH = "SIGI_RH"
const ROLE_FORMS = "SIGI_FORMS"

const ROLE_POR_PERMISSAO: Record<Permissao, string[]> = {
  // Administração (grupos/usuários) - restrita ao SIGI_ADMIN
  [PERMISSOES.admin.gerenciar]: [ROLE_ADMIN],
  // Inspeção
  [PERMISSOES.inspecao.consultar]: [ROLE_ADMIN, ROLE_INSPECAO],
  [PERMISSOES.inspecao.criar]: [ROLE_ADMIN],
  [PERMISSOES.inspecao.editar]: [ROLE_ADMIN],
  [PERMISSOES.inspecao.excluir]: [ROLE_ADMIN],
  [PERMISSOES.inspecao.emitir]: [ROLE_ADMIN],
  [PERMISSOES.inspecao.notificar]: [ROLE_ADMIN],
  [PERMISSOES.inspecao.agendar]: [ROLE_ADMIN],
  [PERMISSOES.inspecao.sortear]: [ROLE_ADMIN],
  [PERMISSOES.inspecao.groupassociation]: [ROLE_ADMIN],
  // Agendamento
  [PERMISSOES.agendamento.consultar]: [ROLE_ADMIN, ROLE_AGENDAMENTO],
  [PERMISSOES.agendamento.criar]: [ROLE_ADMIN],
  [PERMISSOES.agendamento.editar]: [ROLE_ADMIN],
  [PERMISSOES.agendamento.cancelar]: [ROLE_ADMIN],
  [PERMISSOES.agendamento.notificar]: [ROLE_ADMIN],
  [PERMISSOES.agendamento.excluir]: [ROLE_ADMIN],
  [PERMISSOES.agendamento.groupassociation]: [ROLE_ADMIN],
  // EPI
  [PERMISSOES.epi.consultar]: [ROLE_ADMIN, ROLE_EPI],
  [PERMISSOES.epi.solicitar]: [ROLE_ADMIN],
  [PERMISSOES.epi.gerenciar]: [ROLE_ADMIN],
  [PERMISSOES.epi.entregar]: [ROLE_ADMIN],
  [PERMISSOES.epi.descartar]: [ROLE_ADMIN],
  [PERMISSOES.epi.estoque]: [ROLE_ADMIN],
  [PERMISSOES.epi.importar]: [ROLE_ADMIN],
  // Relatório — sem baseline própria (fica sob o perfil/subrole do módulo)
  [PERMISSOES.relatorio.consultar]: [ROLE_ADMIN],
  [PERMISSOES.relatorio.gerar]: [ROLE_ADMIN],
  [PERMISSOES.relatorio.baixar]: [ROLE_ADMIN],
  // Checklist / PbDoc / Seguir — permissão própria (espelha <Mod>Permissions)
  [PERMISSOES.checklist.consultar]: [ROLE_ADMIN, ROLE_CHECKLIST],
  [PERMISSOES.checklist.groupassociation]: [ROLE_ADMIN],
  [PERMISSOES.pbdoc.consultar]: [ROLE_ADMIN, ROLE_PBDOC],
  [PERMISSOES.seguir.consultar]: [ROLE_ADMIN, ROLE_SEGUIR],
  // Almoxarifado
  [PERMISSOES.almoxarifado.consultar]: [ROLE_ADMIN, ROLE_ALMOXARIFADO],
  [PERMISSOES.almoxarifado.categoriaGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.equipamentoCriar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.equipamentoEditar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.estoqueGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.movimentar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.solicitar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.aprovar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.entregar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.relatorio]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.grupoGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.almoxarifado.groupassociation]: [ROLE_ADMIN],
  // Cadastro (localidades/cidades — baseline SIGI_CADASTRO concede só a consulta)
  [PERMISSOES.cadastro.localidadeConsultar]: [ROLE_ADMIN, ROLE_CADASTRO],
  [PERMISSOES.cadastro.localidadeGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.cadastro.cidadeConsultar]: [ROLE_ADMIN, ROLE_CADASTRO],
  [PERMISSOES.cadastro.cidadeGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.cadastro.empresaConsultar]: [ROLE_ADMIN, ROLE_CADASTRO],
  [PERMISSOES.cadastro.empresaGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.cadastro.institucionalConsultar]: [ROLE_ADMIN, ROLE_CADASTRO],
  [PERMISSOES.cadastro.institucionalGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.cadastro.expedienteConsultar]: [ROLE_ADMIN, ROLE_CADASTRO],
  [PERMISSOES.cadastro.expedienteGerenciar]: [ROLE_ADMIN],
  [PERMISSOES.cadastro.documentoConsultar]: [ROLE_ADMIN, ROLE_CADASTRO],
  [PERMISSOES.cadastro.documentoGerenciar]: [ROLE_ADMIN],
  // Incidentes (baseline SIGI_INCIDENTE concede só a consulta)
  [PERMISSOES.incidente.consultar]: [ROLE_ADMIN, ROLE_INCIDENTE],
  [PERMISSOES.incidente.criar]: [ROLE_ADMIN],
  [PERMISSOES.incidente.editar]: [ROLE_ADMIN],
  [PERMISSOES.incidente.excluir]: [ROLE_ADMIN],
  [PERMISSOES.incidente.investigar]: [ROLE_ADMIN],
  // RH (baseline SIGI_RH concede só a consulta)
  [PERMISSOES.rh.consultar]: [ROLE_ADMIN, ROLE_RH],
  [PERMISSOES.rh.criar]: [ROLE_ADMIN],
  [PERMISSOES.rh.editar]: [ROLE_ADMIN],
  [PERMISSOES.rh.excluir]: [ROLE_ADMIN],
  [PERMISSOES.rh.importar]: [ROLE_ADMIN],
  // Formulários (baseline SIGI_FORMS concede só a consulta)
  [PERMISSOES.forms.consultar]: [ROLE_ADMIN, ROLE_FORMS],
  [PERMISSOES.forms.criar]: [ROLE_ADMIN],
  [PERMISSOES.forms.editar]: [ROLE_ADMIN],
  [PERMISSOES.forms.excluir]: [ROLE_ADMIN],
  [PERMISSOES.forms.publicar]: [ROLE_ADMIN],
  [PERMISSOES.forms.assinar]: [ROLE_ADMIN],
  [PERMISSOES.forms.deferir]: [ROLE_ADMIN],
  [PERMISSOES.forms.funcionarioListar]: [ROLE_ADMIN],
  [PERMISSOES.forms.notification]: [ROLE_ADMIN],
  [PERMISSOES.forms.groupassociation]: [ROLE_ADMIN],
}

export function usePermissions() {
  const roles = useSessionStore((s) => s.roles)
  const permissions = useSessionStore((s) => s.permissions)
  const grupoAtivo = useSessionStore((s) => s.grupoAtivo)

  const isAdmin = roles.includes(ROLE_ADMIN)
  // Com grupo ativo, o backend aplica as permissões DAQUELE grupo. No modo
  // DEFINIR elas substituem as do token; HERDAR mantém as permissões globais.
  const permissoesEfetivas = !isAdmin && grupoAtivo?.permissaoModo === "DEFINIR"
    ? (grupoAtivo.permissoes ?? [])
    : (grupoAtivo?.permissoes ?? permissions)
  const herdaPerfis = isAdmin || grupoAtivo?.permissaoModo !== "DEFINIR"

  const has = (permissao: Permissao): boolean => {
    // Permissões efetivas do grupo ativo (ou do /me quando não há grupo)…
    if (permissoesEfetivas.includes(permissao)) {
      return true
    }
    if (!herdaPerfis) {
      return false
    }
    // …ou baseline do módulo (SIGI_<MODULO>) / admin.
    const allowed = ROLE_POR_PERMISSAO[permissao]
    return roles.some((role) => allowed.includes(role))
  }
  return { roles, has, isAdmin, grupoAtivo }
}

export type { Permissao }
